import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, DashboardSummary } from '../api/client';

const statusColors: Record<string, string> = {
  lead: 'bg-blue-100 text-blue-800',
  contactado: 'bg-yellow-100 text-yellow-800',
  en_negociacion: 'bg-orange-100 text-orange-800',
  cliente: 'bg-green-100 text-green-800',
  perdido: 'bg-red-100 text-red-800',
};

const statusLabels: Record<string, string> = {
  lead: 'Lead',
  contactado: 'Contactado',
  en_negociacion: 'En Negociación',
  cliente: 'Cliente',
  perdido: 'Perdido',
};

export default function Dashboard() {
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const response = await api.dashboard.summary();
      setSummary(response.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amazon-orange"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 text-red-600 p-4 rounded-lg">
        {error}
      </div>
    );
  }

  const funnel = summary?.funnel;

  return (
    <div className="space-y-6">
      <h1 className="text-xl sm:text-2xl font-bold text-gray-800">📊 Dashboard</h1>

      {/* Tarjetas de métricas */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 sm:gap-4">
        <MetricCard title="Leads" value={funnel?.leads || 0} color="blue" icon="🎯" />
        <MetricCard title="Contactados" value={funnel?.contactados || 0} color="yellow" icon="📞" />
        <MetricCard title="Negociación" value={funnel?.enNegociacion || 0} color="orange" icon="💼" />
        <MetricCard title="Clientes" value={funnel?.clientes || 0} color="green" icon="✅" />
        <MetricCard title="Perdidos" value={funnel?.perdidos || 0} color="red" icon="❌" />
      </div>

      {/* Embudo visual */}
      <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6">
        <h2 className="text-lg font-semibold mb-4">🔄 Embudo de Ventas</h2>
        <div className="space-y-3">
          <FunnelBar label="Leads" value={funnel?.leads || 0} total={funnel?.total || 1} color="bg-blue-500" />
          <FunnelBar label="Contactados" value={funnel?.contactados || 0} total={funnel?.total || 1} color="bg-yellow-500" />
          <FunnelBar label="En Negociación" value={funnel?.enNegociacion || 0} total={funnel?.total || 1} color="bg-orange-500" />
          <FunnelBar label="Clientes" value={funnel?.clientes || 0} total={funnel?.total || 1} color="bg-green-500" />
        </div>
        <div className="mt-4 text-sm text-gray-500">
          Tasa de conversión: <span className="font-semibold text-amazon-orange">{summary?.conversionRate}</span>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Tareas de hoy */}
        <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">📅 Tareas de Hoy</h2>
            <Link to="/tasks" className="text-amazon-orange hover:underline text-sm">
              Ver todas →
            </Link>
          </div>
          <div className="space-y-2">
            {summary?.upcomingTasks?.length === 0 ? (
              <p className="text-gray-500 text-sm">No hay tareas pendientes</p>
            ) : (
              summary?.upcomingTasks?.map((task) => (
                <div key={task.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <span className="text-gray-400">☐</span>
                  <div className="flex-1">
                    <p className="text-sm font-medium">{task.title}</p>
                    {task.contact_name && (
                      <p className="text-xs text-gray-500">→ {task.contact_name}</p>
                    )}
                  </div>
                  {task.due_date && (
                    <span className="text-xs text-gray-400">{task.due_date}</span>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        {/* Últimos contactos */}
        <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">👥 Últimos Contactos</h2>
            <Link to="/contacts" className="text-amazon-orange hover:underline text-sm">
              Ver todos →
            </Link>
          </div>
          <div className="space-y-2">
            {summary?.recentContacts?.map((contact) => (
              <Link
                key={contact.id}
                to={`/contacts/${contact.id}`}
                className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition"
              >
                <div className="w-10 h-10 bg-amazon-orange rounded-full flex items-center justify-center text-white font-bold">
                  {contact.name.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium">{contact.name}</p>
                  <p className="text-xs text-gray-500">{contact.company || 'Sin empresa'}</p>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full ${statusColors[contact.status]}`}>
                  {statusLabels[contact.status]}
                </span>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function MetricCard({ title, value, color, icon }: { title: string; value: number; color: string; icon: string }) {
  const colorClasses: Record<string, string> = {
    blue: 'bg-blue-50 border-blue-200',
    yellow: 'bg-yellow-50 border-yellow-200',
    orange: 'bg-orange-50 border-orange-200',
    green: 'bg-green-50 border-green-200',
    red: 'bg-red-50 border-red-200',
  };

  return (
    <div className={`rounded-xl border-2 p-4 ${colorClasses[color]}`}>
      <div className="text-2xl mb-2">{icon}</div>
      <div className="text-2xl font-bold text-gray-800">{value}</div>
      <div className="text-sm text-gray-600">{title}</div>
    </div>
  );
}

function FunnelBar({ label, value, total, color }: { label: string; value: number; total: number; color: string }) {
  const percentage = total > 0 ? (value / total) * 100 : 0;

  return (
    <div className="flex items-center gap-2 sm:gap-4">
      <div className="w-20 sm:w-32 shrink-0 text-xs sm:text-sm text-gray-600 truncate">{label}</div>
      <div className="flex-1 bg-gray-100 rounded-full h-5 sm:h-6 overflow-hidden">
        <div
          className={`h-full ${color} transition-all duration-500`}
          style={{ width: `${percentage}%` }}
        />
      </div>
      <div className="w-10 sm:w-16 shrink-0 text-right text-xs sm:text-sm font-medium">{value}</div>
    </div>
  );
}
